if (!customElements.get('product-shipping-calculator')) {
  // const REFRIGERATED_CEP_RANGES = [
  //   [1000000, 5999999],
  //   [8000000, 8499999],
  // ];
  const REFRIGERATED_CEP_RANGES = [
    [1000000, 99000000]
  ];

  const normalizeCep = (value) => String(value || '').replace(/\D/g, '').slice(0, 8);

  const formatCep = (value) => {
    const cep = normalizeCep(value);
    return cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
  };

  const toBoolean = (value) => value === true || value === 'true';

  const toGid = (type, id) => {
    if (!id) return null;
    const value = String(id);
    return value.startsWith('gid://') ? value : `gid://shopify/${type}/${value}`;
  };

  const parseJsonScript = (root, selector, fallback = null) => {
    try {
      const script = root?.querySelector(selector);
      return script ? JSON.parse(script.textContent) : fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const parseConfiguredLocations = (root) => {
    const script = root?.querySelector('[data-configured-store-locations]');
    const rawValue = script?.textContent?.trim() || '';
    if (!rawValue) return [];

    try {
      const parsedValue = JSON.parse(rawValue);
      if (Array.isArray(parsedValue)) return parsedValue;
    } catch (_error) {
      // Also support the simple "Name | CEP | WhatsApp" format from the theme editor.
    }

    return rawValue
      .split(/\r?\n/)
      .map((line, index) => {
        const [name, zip, phone, address1, city, province] = line.split('|').map((value) => value.trim());
        if (!name || !zip) return null;
        return {
          id: `configured-${index + 1}`,
          name,
          zip,
          phone: phone || '',
          address1: address1 || '',
          city: city || '',
          province: province || '',
        };
      })
      .filter(Boolean);
  };

  class ProductShippingCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.form = this.querySelector('[data-shipping-form]');
      this.zipInput = this.querySelector('[data-shipping-zip]');
      this.submitButton = this.querySelector('[data-shipping-submit]');
      this.status = this.querySelector('[data-shipping-status]');
      this.results = this.querySelector('[data-shipping-results]');
      this.ratesSection = this.querySelector('[data-shipping-rates-section]');
      this.ratesContainer = this.querySelector('[data-shipping-rates]');
      this.storesSection = this.querySelector('[data-nearby-stores-section]');
      this.storesContainer = this.querySelector('[data-nearby-stores]');
      this.storesEmpty = this.querySelector('[data-nearby-stores-empty]');
      this.mixedNote = this.querySelector('[data-mixed-shipping-note]');
      const configuredLocations = parseConfiguredLocations(this);
      const automaticLocations = parseJsonScript(this, '[data-initial-store-locations]', []);
      this.configuredLocations = Array.isArray(configuredLocations) ? configuredLocations : [];
      this.initialLocations = this.configuredLocations.length
        ? this.configuredLocations
        : Array.isArray(automaticLocations)
          ? automaticLocations
          : [];
      this.onSubmit = this.onSubmit.bind(this);
      this.onZipInput = this.onZipInput.bind(this);

      this.form?.addEventListener('submit', this.onSubmit);
      this.zipInput?.addEventListener('input', this.onZipInput);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.zipInput?.removeEventListener('input', this.onZipInput);
      this.initialized = false;
    }

    onZipInput() {
      const formatted = formatCep(this.zipInput.value);
      if (this.zipInput.value !== formatted) this.zipInput.value = formatted;
    }

    async onSubmit(event) {
      event.preventDefault();

      const zip = normalizeCep(this.zipInput?.value);
      if (zip.length !== 8) {
        this.setStatus(this.dataset.errorInvalidCep, true);
        this.zipInput?.focus();
        return;
      }

      this.clearResults();
      this.currentCartPromise = null;
      this.setLoading(true);
      this.setStatus('Calculando frete…');

      try {
        const address = await this.fetchAddress(zip);
        const shouldValidateRefrigerated =
          toBoolean(this.dataset.productRefrigerated) ||
          (toBoolean(this.dataset.includeCurrentCart) && toBoolean(this.dataset.cartHasRefrigerated));

        const shippingPromise = shouldValidateRefrigerated && !this.isRefrigeratedCepAvailable(zip)
          ? Promise.reject(new Error(this.dataset.errorRefrigerated))
          : this.fetchShippingQuote(address, zip);

        const storesPromise = toBoolean(this.dataset.showNearbyStores)
          ? this.findNearbyStores(address, zip)
          : Promise.resolve([]);

        const [shippingResult, storesResult] = await Promise.allSettled([shippingPromise, storesPromise]);

        if (shippingResult.status === 'fulfilled') {
          this.renderRates(shippingResult.value);
          this.setStatus('');
        } else {
          console.error('Product shipping quote failed', shippingResult.reason);
          this.setStatus(shippingResult.reason?.message || this.dataset.errorGeneric, true);
        }

        if (storesResult.status === 'fulfilled') {
          this.renderStores(storesResult.value);
        } else {
          console.warn('Nearby store lookup failed', storesResult.reason);
        }

        this.updateResultsVisibility();
      } catch (error) {
        console.error('Product shipping calculator failed', error);
        this.setStatus(error?.message || this.dataset.errorGeneric, true);
      } finally {
        this.setLoading(false);
      }
    }

    isRefrigeratedCepAvailable(zip) {
      const value = Number(normalizeCep(zip));
      return REFRIGERATED_CEP_RANGES.some(([minimum, maximum]) => value >= minimum && value <= maximum);
    }

    async fetchAddress(zip) {
      const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(this.dataset.errorInvalidCep);

      const address = await response.json();
      if (address.erro || !address.uf) throw new Error(this.dataset.errorInvalidCep);
      return address;
    }

    getCurrentSelection() {
      const productForm = document.getElementById(this.dataset.productFormId);
      const variantInput = productForm?.querySelector('input[name="id"]');
      const quantityInput =
        productForm?.querySelector('input[name="quantity"]') ||
        document.querySelector(`input[name="quantity"][form="${this.dataset.productFormId}"]`) ||
        document.querySelector(`#Quantity-${this.dataset.sectionId}`);
      const sellingPlanInput = productForm?.querySelector('[name="selling_plan"]:checked, [name="selling_plan"]');
      const requestedQuantity = Number(quantityInput?.value || 1);

      return {
        variantId: variantInput?.value || this.dataset.initialVariantId,
        quantity: Number.isFinite(requestedQuantity) ? Math.max(1, Math.floor(requestedQuantity)) : 1,
        sellingPlanId: sellingPlanInput?.value || null,
      };
    }

    addQuoteLine(lines, variantId, quantity, sellingPlanId = null) {
      const merchandiseId = toGid('ProductVariant', variantId);
      if (!merchandiseId || Number(quantity) <= 0) return;

      const normalizedSellingPlanId = toGid('SellingPlan', sellingPlanId);
      const key = `${merchandiseId}:${normalizedSellingPlanId || ''}`;
      const existing = lines.get(key);

      if (existing) {
        existing.quantity += Number(quantity);
        return;
      }

      const line = {
        merchandiseId,
        quantity: Number(quantity),
      };
      if (normalizedSellingPlanId) line.sellingPlanId = normalizedSellingPlanId;
      lines.set(key, line);
    }

    async buildQuoteLines() {
      const lines = new Map();
      const cartVariantIds = new Set();

      if (toBoolean(this.dataset.includeCurrentCart)) {
        const cart = await this.fetchCurrentCart();
        if (cart) {
          cart.items?.forEach((item) => {
            if (item.requires_shipping === false) return;
            cartVariantIds.add(String(item.variant_id));
            this.addQuoteLine(
              lines,
              item.variant_id,
              item.quantity,
              item.selling_plan_allocation?.selling_plan?.id
            );
          });
        }
      }

      const selection = this.getCurrentSelection();
      const selectionAlreadyInCart = cartVariantIds.has(String(selection.variantId));

      if (!selectionAlreadyInCart) {
        this.addQuoteLine(lines, selection.variantId, selection.quantity, selection.sellingPlanId);
      }

      return Array.from(lines.values());
    }

    async fetchCurrentCart() {
      if (!this.currentCartPromise) {
        this.currentCartPromise = fetch(this.dataset.cartJsonUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null);
      }

      return this.currentCartPromise;
    }

    async fetchShippingQuote(address, zip) {
      const checkoutCartRates = await this.fetchCheckoutCartRates(address, zip);
      if (checkoutCartRates?.length) return [this.createCheckoutRateGroup(checkoutCartRates)];

      const lines = await this.buildQuoteLines();
      if (!lines.length) throw new Error(this.dataset.errorNoRates);

      const input = {
        lines,
        buyerIdentity: { countryCode: 'BR' },
        delivery: {
          addresses: [
            {
              selected: true,
              oneTimeUse: true,
              address: {
                deliveryAddress: {
                  address1: address.logradouro || `CEP ${formatCep(zip)}`,
                  city: address.localidade,
                  provinceCode: address.uf,
                  countryCode: 'BR',
                  zip,
                },
              },
            },
          ],
        },
      };

      const payloads = await this.requestStorefront(this.createQuoteMutation(), { input }, true);
      const initialPayload = payloads.find((payload) => payload.data?.cartCreate);
      const cartCreate = initialPayload?.data?.cartCreate;
      const userErrors = cartCreate?.userErrors || [];

      if (userErrors.length) {
        throw new Error(userErrors.map((error) => error.message).join(' '));
      }

      const cartId = cartCreate?.cart?.id;
      let groups = cartCreate?.cart?.deliveryGroups?.nodes || [];
      const deferredErrors = [];

      payloads.forEach((payload) => {
        payload.errors?.forEach((error) => deferredErrors.push(error.message));
        payload.incremental?.forEach((increment) => {
          increment.errors?.forEach((error) => deferredErrors.push(error.message));
          if (increment.data?.deliveryGroups?.nodes) groups = increment.data.deliveryGroups.nodes;
          if (increment.data?.cart?.deliveryGroups?.nodes) groups = increment.data.cart.deliveryGroups.nodes;
        });
      });

      if ((!groups.length || !groups.some((group) => group.deliveryOptions?.length)) && cartId) {
        groups = await this.fetchStaticDeliveryGroups(cartId);
      }

      if (!groups.length || !groups.some((group) => group.deliveryOptions?.length)) {
        if (deferredErrors.length) console.warn('Carrier rate lookup warnings', deferredErrors);
        throw new Error(this.dataset.errorNoRates);
      }

      return this.normalizeDeliveryGroups(groups);
    }

    async fetchCheckoutCartRates(address, zip) {
      if (!toBoolean(this.dataset.includeCurrentCart)) return null;

      const cart = await this.fetchCurrentCart();
      const selection = this.getCurrentSelection();
      const selectionIsInCart = cart?.items?.some(
        (item) => item.requires_shipping !== false && String(item.variant_id) === String(selection.variantId)
      );

      if (!selectionIsInCart) return null;

      let rootUrl = this.dataset.rootUrl || '/';
      if (!rootUrl.endsWith('/')) rootUrl += '/';

      const parameters = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': address.uf,
      });
      const prepareUrl = `${rootUrl}cart/prepare_shipping_rates.json?${parameters.toString()}`;
      const ratesUrl = `${rootUrl}cart/async_shipping_rates.json?${parameters.toString()}`;

      try {
        const prepareResponse = await fetch(prepareUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!prepareResponse.ok) return null;

        for (let attempt = 0; attempt < 12; attempt += 1) {
          const ratesResponse = await fetch(ratesUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });

          if (ratesResponse.ok) {
            const payload = await ratesResponse.json();
            if (Array.isArray(payload?.shipping_rates)) return payload.shipping_rates;
          }

          await new Promise((resolve) => window.setTimeout(resolve, 400));
        }
      } catch (error) {
        console.warn('Checkout cart rate lookup failed; using isolated quote instead.', error);
      }

      return null;
    }

    createCheckoutRateGroup(rates) {
      const uniqueRates = this.deduplicateDeliveryOptions(
        rates.map((rate) => ({
          title: rate.name || rate.title || 'Entrega',
          description: this.getCheckoutRateDescription(rate),
          deliveryMethodType: 'SHIPPING',
          estimatedCost: {
            amount: String(rate.price || 0),
            currencyCode: rate.currency || window.Shopify?.currency?.active || 'BRL',
          },
        }))
      );

      return {
        id: 'checkout-cart-rates',
        groupType: 'ONE_TIME_PURCHASE',
        cartLines: { nodes: [] },
        deliveryOptions: uniqueRates,
      };
    }

    deduplicateDeliveryOptions(options) {
      const seenTitles = new Set();

      return (options || []).filter((option) => {
        const normalizedTitle = String(option.title || 'Entrega')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim();

        if (seenTitles.has(normalizedTitle)) return false;
        seenTitles.add(normalizedTitle);
        return true;
      });
    }

    getCheckoutRateDescription(rate) {
      const deliveryDays = this.formatDeliveryDays(rate.delivery_days);
      if (deliveryDays) return deliveryDays;

      const deliveryRangeDays = this.formatDeliveryDays(rate.delivery_range);
      if (deliveryRangeDays) return deliveryRangeDays;

      const configuredDeliveryDays = this.getConfiguredDeliveryDays(rate.name || rate.title);
      if (configuredDeliveryDays) return configuredDeliveryDays;

      if (rate.description) return rate.description;

      const deliveryRangeDates = this.formatDeliveryDateRange(rate.delivery_range);
      if (deliveryRangeDates) return deliveryRangeDates;

      const deliveryDate = this.formatDeliveryDate(rate.delivery_date);
      if (deliveryDate) return `Entrega prevista para ${deliveryDate}`;

      return '';
    }

    getConfiguredDeliveryDays(title) {
      const normalizedTitle = String(title || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      if (normalizedTitle.includes('economico')) {
        return this.formatDeliveryDays(this.dataset.economicDeliveryDays || 2);
      }

      if (normalizedTitle.includes('rapido')) {
        return this.formatDeliveryDays(this.dataset.fastDeliveryDays || 1);
      }

      if (normalizedTitle.includes('padrao')) {
        return this.formatDeliveryDays([
          this.dataset.standardDeliveryMinDays || 3,
          this.dataset.standardDeliveryMaxDays || 5,
        ]);
      }

      return '';
    }

    formatDeliveryDays(value) {
      if (value === null || value === undefined || value === '') return '';

      let minimum;
      let maximum;

      if (Array.isArray(value)) {
        [minimum, maximum = minimum] = value;
      } else if (typeof value === 'object') {
        minimum = value.minimum ?? value.min ?? value.from;
        maximum = value.maximum ?? value.max ?? value.to ?? minimum;
      } else {
        minimum = value;
        maximum = value;
      }

      const minimumDays = Number(minimum);
      const maximumDays = Number(maximum);
      if (!Number.isFinite(minimumDays) || !Number.isFinite(maximumDays)) return '';

      const firstDay = Math.max(0, Math.min(minimumDays, maximumDays));
      const lastDay = Math.max(0, Math.max(minimumDays, maximumDays));

      if (lastDay === 0) return 'Entrega hoje';
      if (firstDay === 0) return `Até ${lastDay} ${lastDay === 1 ? 'dia útil' : 'dias úteis'}`;
      if (firstDay === lastDay) return `${firstDay} ${firstDay === 1 ? 'dia útil' : 'dias úteis'}`;

      return `${firstDay} a ${lastDay} dias úteis`;
    }

    formatDeliveryDateRange(value) {
      if (!Array.isArray(value) || value.length < 2) return '';

      const minimum = this.formatDeliveryDate(value[0]);
      const maximum = this.formatDeliveryDate(value[1]);
      if (!minimum || !maximum) return '';
      if (minimum === maximum) return `Entrega prevista para ${minimum}`;

      return `Entrega entre ${minimum} e ${maximum}`;
    }

    formatDeliveryDate(value) {
      if (typeof value !== 'string') return '';

      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return '';

      return `${match[3]}/${match[2]}`;
    }

    normalizeDeliveryGroups(groups) {
      const normalizedGroups = new Map();

      groups.forEach((group, index) => {
        const lines = group.cartLines?.nodes || [];
        const lineSignature = Array.from(
          new Set(
            lines.map((line) => {
              const variantId = line.merchandise?.id || '';
              const productTitle = line.merchandise?.product?.title || '';
              return variantId || productTitle;
            })
          )
        )
          .filter(Boolean)
          .sort()
          .join('|');
        const signature = lineSignature
          ? `${group.groupType || 'delivery'}:${lineSignature}`
          : `${group.id || `delivery-${index}`}`;
        const existingGroup = normalizedGroups.get(signature);

        if (!existingGroup) {
          normalizedGroups.set(signature, group);
          return;
        }

        const existingOptions = existingGroup.deliveryOptions?.length || 0;
        const currentOptions = group.deliveryOptions?.length || 0;

        if (currentOptions > existingOptions) {
          normalizedGroups.set(signature, group);
        }
      });

      const uniqueGroups = Array.from(normalizedGroups.values());

      const consolidatedGroups = uniqueGroups.filter((group, groupIndex) => {
        const groupVariants = new Set(
          (group.cartLines?.nodes || [])
            .map((line) => line.merchandise?.id || line.merchandise?.product?.title)
            .filter(Boolean)
        );
        const groupOptionCount = group.deliveryOptions?.length || 0;

        return !uniqueGroups.some((candidate, candidateIndex) => {
          if (candidateIndex === groupIndex || candidate.groupType !== group.groupType) return false;
          if ((candidate.deliveryOptions?.length || 0) <= groupOptionCount) return false;

          const candidateVariants = new Set(
            (candidate.cartLines?.nodes || [])
              .map((line) => line.merchandise?.id || line.merchandise?.product?.title)
              .filter(Boolean)
          );

          return groupVariants.size > 0 && Array.from(groupVariants).every((variant) => candidateVariants.has(variant));
        });
      });

      consolidatedGroups.sort((firstGroup, secondGroup) => {
        const optionDifference =
          (secondGroup.deliveryOptions?.length || 0) - (firstGroup.deliveryOptions?.length || 0);
        if (optionDifference !== 0) return optionDifference;

        return (secondGroup.cartLines?.nodes?.length || 0) - (firstGroup.cartLines?.nodes?.length || 0);
      });

      return consolidatedGroups.slice(0, 1).map((group) => ({
        ...group,
        deliveryOptions: this.deduplicateDeliveryOptions(group.deliveryOptions),
      }));
    }

    createQuoteMutation() {
      return `
        mutation ProductShippingQuote($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              id
              ...ShippingDeliveryGroups @defer
            }
            userErrors {
              field
              message
              code
            }
            warnings {
              code
              message
            }
          }
        }

        fragment ShippingDeliveryGroups on Cart {
          deliveryGroups(first: 10, withCarrierRates: true) {
            nodes {
              ...ShippingDeliveryGroupFields
            }
          }
        }

        fragment ShippingDeliveryGroupFields on CartDeliveryGroup {
          id
          groupType
          cartLines(first: 20) {
            nodes {
              ... on CartLine {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    product {
                      title
                    }
                  }
                }
              }
            }
          }
          deliveryOptions {
            title
            description
            deliveryMethodType
            estimatedCost {
              amount
              currencyCode
            }
          }
        }
      `;
    }

    async fetchStaticDeliveryGroups(cartId) {
      const query = `
        query ProductShippingStaticRates($cartId: ID!) {
          cart(id: $cartId) {
            deliveryGroups(first: 10) {
              nodes {
                id
                groupType
                cartLines(first: 20) {
                  nodes {
                    ... on CartLine {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          product {
                            title
                          }
                        }
                      }
                    }
                  }
                }
                deliveryOptions {
                  title
                  description
                  deliveryMethodType
                  estimatedCost {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      `;
      const [payload] = await this.requestStorefront(query, { cartId }, false);
      return payload?.data?.cart?.deliveryGroups?.nodes || [];
    }

    async requestStorefront(query, variables, acceptsDefer) {
      const apiVersion = this.dataset.storefrontApiVersion || '2026-07';
      const response = await fetch(`${window.location.origin}/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: acceptsDefer ? 'multipart/mixed; deferSpec=20220824, application/json' : 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) throw new Error(this.dataset.errorGeneric);
      return this.parseStorefrontResponse(response);
    }

    async parseStorefrontResponse(response) {
      const contentType = response.headers.get('content-type') || '';
      const body = await response.text();

      if (!contentType.includes('multipart/mixed')) {
        const payload = JSON.parse(body);
        if (payload.errors?.length && !payload.data) {
          throw new Error(payload.errors.map((error) => error.message).join(' '));
        }
        return [payload];
      }

      const boundaryMatch = contentType.match(/boundary="?([^=";]+)"?/i);
      if (!boundaryMatch) throw new Error(this.dataset.errorGeneric);

      return body
        .split(`--${boundaryMatch[1]}`)
        .map((part) => {
          const sections = part.split(/\r?\n\r?\n/);
          if (sections.length < 2) return null;
          const json = sections.slice(1).join('\n\n').trim();
          if (!json || json === '--') return null;
          try {
            return JSON.parse(json);
          } catch (_error) {
            return null;
          }
        })
        .filter(Boolean);
    }

    async findNearbyStores(address, zip) {
      const selection = this.getCurrentSelection();
      const [customerCoordinates, locations] = await Promise.all([
        this.geocodeAddress(
          {
            zip,
            address1: address.logradouro,
            neighborhood: address.bairro,
            city: address.localidade,
            provinceCode: address.uf,
            country: 'Brasil',
          },
          `customer-${zip}`
        ),
        this.fetchStoreLocations(selection.variantId),
      ]);

      if (!customerCoordinates || !locations.length) return [];

      const stores = [];
      for (const location of locations) {
        let coordinates = this.normalizeCoordinates(location.latitude, location.longitude);
        if (!coordinates) coordinates = await this.geocodeAddress(location, `store-${location.id || location.zip}`);
        if (!coordinates) continue;

        stores.push({
          ...location,
          distance: this.calculateDistance(customerCoordinates, coordinates) * 1.2,
        });
      }

      const radius = Number(this.dataset.storeRadius || 0);
      const limit = Math.max(1, Number(this.dataset.storeLimit || 3));

      return stores
        .filter((store) => radius <= 0 || store.distance <= radius)
        .sort((first, second) => first.distance - second.distance)
        .slice(0, limit);
    }

    async fetchStoreLocations(variantId) {
      if (this.configuredLocations.length) return this.configuredLocations;
      if (!variantId) return this.initialLocations;

      try {
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) rootUrl += '/';
        const response = await fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) return this.initialLocations;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sectionLocations = parseJsonScript(html, '[data-store-location-data]', null);
        return sectionLocations?.length ? sectionLocations : this.initialLocations;
      } catch (_error) {
        return this.initialLocations;
      }
    }

    normalizeCoordinates(latitude, longitude) {
      if (latitude === null || latitude === undefined || latitude === '') return null;
      if (longitude === null || longitude === undefined || longitude === '') return null;

      const lat = Number(latitude);
      const lon = Number(longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      if (Math.abs(lat) > 90 || Math.abs(lon) > 180 || (lat === 0 && lon === 0)) return null;
      return { lat, lon };
    }

    async geocodeAddress(address, cacheSuffix) {
      const cacheKey = `emporio-store-geocode-v2-${cacheSuffix}`;
      const cached = this.readSessionCache(cacheKey);
      if (cached) return cached;

      const cepCoordinates = await this.fetchCepCoordinates(address.zip);
      if (cepCoordinates) {
        this.writeSessionCache(cacheKey, cepCoordinates);
        return cepCoordinates;
      }

      const country = address.country || 'Brasil';
      const province = address.provinceCode || address.province;
      const queryCandidates = [
        [address.address1, address.neighborhood, address.city, province, address.zip, country],
        [address.neighborhood, address.city, province, country],
        [address.city, province, country],
      ]
        .map((terms) => terms.filter(Boolean).join(', '))
        .filter((query, index, queries) => query && queries.indexOf(query) === index);

      for (const queryText of queryCandidates) {
        const query = new URLSearchParams({
          format: 'jsonv2',
          countrycodes: 'br',
          limit: '1',
          q: queryText,
        });

        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/search?${query.toString()}`, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) continue;
          const results = await response.json();
          const coordinates = this.normalizeCoordinates(results?.[0]?.lat, results?.[0]?.lon);
          if (coordinates) {
            this.writeSessionCache(cacheKey, coordinates);
            return coordinates;
          }
        } catch (_error) {
          // Try the next, broader query before giving up.
        }
      }

      return null;
    }

    async fetchCepCoordinates(zip) {
      const normalizedZip = normalizeCep(zip);
      if (normalizedZip.length !== 8) return null;

      try {
        const response = await fetch(`https://cep.awesomeapi.com.br/json/${normalizedZip}`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return null;
        const data = await response.json();
        return this.normalizeCoordinates(data.lat, data.lng);
      } catch (_error) {
        return null;
      }
    }

    readSessionCache(key) {
      try {
        return JSON.parse(sessionStorage.getItem(key));
      } catch (_error) {
        return null;
      }
    }

    writeSessionCache(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (_error) {
        // Storage can be unavailable in strict privacy modes.
      }
    }

    calculateDistance(first, second) {
      const earthRadius = 6371;
      const toRadians = (degrees) => (degrees * Math.PI) / 180;
      const deltaLatitude = toRadians(second.lat - first.lat);
      const deltaLongitude = toRadians(second.lon - first.lon);
      const firstLatitude = toRadians(first.lat);
      const secondLatitude = toRadians(second.lat);
      const haversine =
        Math.sin(deltaLatitude / 2) ** 2 +
        Math.cos(firstLatitude) * Math.cos(secondLatitude) * Math.sin(deltaLongitude / 2) ** 2;

      return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    }

    renderRates(groups) {
      this.ratesContainer.innerHTML = '';
      const groupsWithRates = groups.filter((group) => group.deliveryOptions?.length);

      groupsWithRates.forEach((group, index) => {
        const groupElement = document.createElement('div');
        groupElement.className = 'product-shipping-calculator__delivery-group';

        if (groupsWithRates.length > 1) {
          const productNames = Array.from(
            new Set(
              (group.cartLines?.nodes || [])
                .map((line) => line.merchandise?.product?.title)
                .filter(Boolean)
            )
          );
          const heading = document.createElement('p');
          heading.className = 'product-shipping-calculator__delivery-group-title';
          heading.textContent = `Envio ${index + 1}${productNames.length ? ` — ${productNames.join(', ')}` : ''}`;
          groupElement.appendChild(heading);
        }

        group.deliveryOptions.forEach((rate) => {
          const row = document.createElement('p');
          row.className = 'product-shipping-calculator__rate';
          const rateDescription = rate.description || this.getConfiguredDeliveryDays(rate.title);

          const title = document.createElement('span');
          title.textContent = rate.title || 'Entrega';
          row.appendChild(title);

          if (rateDescription) {
            const description = document.createElement('span');
            description.className = 'product-shipping-calculator__rate-description';
            description.textContent = rateDescription;
            row.appendChild(description);
          }

          const price = document.createElement('span');
          price.textContent = this.formatMoney(rate.estimatedCost);
          row.appendChild(price);
          groupElement.appendChild(row);
        });

        this.ratesContainer.appendChild(groupElement);
      });

      const quoteHasRefrigerated =
        toBoolean(this.dataset.productRefrigerated) ||
        (toBoolean(this.dataset.includeCurrentCart) && toBoolean(this.dataset.cartHasRefrigerated));
      const quoteHasStandard =
        !toBoolean(this.dataset.productRefrigerated) ||
        (toBoolean(this.dataset.includeCurrentCart) && toBoolean(this.dataset.cartHasStandard));
      const showMixedNote = quoteHasRefrigerated && quoteHasStandard;

      if (this.mixedNote) {
        this.mixedNote.hidden = !showMixedNote;
        this.mixedNote.textContent = showMixedNote ? this.dataset.mixedShippingNote : '';
      }

      this.ratesSection.hidden = groupsWithRates.length === 0;
    }

    renderStores(stores) {
      this.storesContainer.innerHTML = '';

      if (!toBoolean(this.dataset.showNearbyStores)) {
        this.storesSection.hidden = true;
        if (this.storesEmpty) this.storesEmpty.hidden = true;
        return;
      }

      if (this.storesEmpty) {
        this.storesEmpty.hidden = stores.length > 0;
      }

      stores.forEach((store) => {
        const storeElement = document.createElement('article');
        storeElement.className = 'product-shipping-calculator__store';

        const name = document.createElement('h5');
        name.className = 'product-shipping-calculator__store-name';
        name.textContent = store.name;
        storeElement.appendChild(name);

        const phone = store.phone || this.dataset.fallbackWhatsapp;
        const whatsappNumber = this.normalizeWhatsapp(phone);
        if (phone && whatsappNumber) {
          const whatsapp = document.createElement('a');
          whatsapp.className = 'product-shipping-calculator__store-whatsapp';
          whatsapp.href = `https://wa.me/${whatsappNumber}`;
          whatsapp.target = '_blank';
          whatsapp.rel = 'noopener';
          whatsapp.innerHTML = `
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
              <path fill="#25D366" d="M12 2a9.8 9.8 0 0 0-8.42 14.82L2.05 22l5.32-1.48A9.93 9.93 0 1 0 12 2Z"/>
              <path fill="#fff" d="M17.36 14.46c-.3-.15-1.77-.87-2.04-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.95 1.17-.18.2-.35.22-.65.07-.3-.15-1.27-.46-2.42-1.48a9.08 9.08 0 0 1-1.67-2.08c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.63-.92-2.23-.24-.58-.49-.5-.68-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z"/>
            </svg>
          `;
          const label = document.createElement('span');
          label.textContent = `WhatsApp: ${phone}`;
          whatsapp.appendChild(label);
          storeElement.appendChild(whatsapp);
        }

        const distance = document.createElement('p');
        distance.className = 'product-shipping-calculator__store-distance';
        distance.textContent = `Distância aproximada: ${Math.max(1, Math.round(store.distance))} km`;
        storeElement.appendChild(distance);
        this.storesContainer.appendChild(storeElement);
      });

      this.storesSection.hidden = false;
    }

    normalizeWhatsapp(phone) {
      let digits = String(phone || '').replace(/\D/g, '');
      if (!digits) return '';
      if (digits.length <= 11) digits = `55${digits}`;
      return digits;
    }

    formatMoney(money) {
      const amount = Number(money?.amount || 0);
      if (amount === 0) return 'Grátis';

      try {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: money?.currencyCode || 'BRL',
        }).format(amount);
      } catch (_error) {
        return `R$ ${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    clearResults() {
      this.ratesContainer.innerHTML = '';
      this.storesContainer.innerHTML = '';
      this.ratesSection.hidden = true;
      this.storesSection.hidden = true;
      this.results.hidden = true;
      if (this.mixedNote) this.mixedNote.hidden = true;
    }

    updateResultsVisibility() {
      this.results.hidden = this.ratesSection.hidden && this.storesSection.hidden;
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('is-error', isError);
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', String(isLoading));
      const defaultLabel = this.submitButton.querySelector('[data-label-default]');
      const loadingLabel = this.submitButton.querySelector('[data-label-loading]');
      if (defaultLabel) defaultLabel.hidden = isLoading;
      if (loadingLabel) loadingLabel.hidden = !isLoading;
    }
  }

  customElements.define('product-shipping-calculator', ProductShippingCalculator);
}
